import { ReactNode } from 'react';
import { OverrideType } from '@weave/schema-gen-ts/dist/schemas/phone/override/v1/override.pb';
import { FeatureFlagQueries } from '@frontend/api-feature-flags';
import { ForwardingNumberTypes } from '@frontend/api-forwarding-number';
import { VoicemailConfigAPI } from '@frontend/api-voicemail-boxes';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useQuery } from '@frontend/react-query-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Heading, PlusIconSmall, SearchIcon, SecondaryButton, Text, TextLink, styles } from '@frontend/design-system';
import { queryKeys } from '../../query-keys';
import { usePhoneSettingsShallowStore } from '../../store/settings';
import {
  emptyOverrideSectionStyle,
  overrideSectionChipStyle,
  overrideSectionHeadingStyle,
} from '../../views/settings/overrides-styles';
import { CachedAudioScrubber } from '../common/cached-audio-scrubber';

export const EmptyOverrideSection = ({
  openSettingTray,
  className,
}: {
  openSettingTray: () => void;
  className?: string;
}) => {
  const { t } = useTranslation('phone', { keyPrefix: 'overrides' });

  return (
    <section css={emptyOverrideSectionStyle} className={className}>
      <div css={{ display: 'flex', gap: theme.spacing(1), alignItems: 'center' }}>
        <SearchIcon size={16} color='subdued' />
        <Text color='light'>{t('No Active Override')}</Text>
      </div>
      <TextLink
        onClick={openSettingTray}
        css={{ display: 'flex', gap: theme.spacing(1), width: 'auto', alignItems: 'center' }}
        trackingId='phone-portal-btn-openOverrideModal'
        weight='bold'
      >
        <PlusIconSmall /> {t('Create Override')}
      </TextLink>
    </section>
  );
};

type OverrideSectionProps = {
  type: OverrideType;
  statusChip: ReactNode;
  startTime?: string;
  endTime?: string;
  mailboxId?: string;
  media?: {
    id: string;
    name: string;
    path: string;
  };
  forwardingNumber?: ForwardingNumberTypes.FwdNumberModel;

  openRemoveModal: () => void;
  openSettingModal: () => void;
  className?: string;
};

const GridArea = ({ area, title, children }: { area: string; title: ReactNode; children: ReactNode }) => (
  <div css={{ gridArea: area }}>
    <Text as='dt' size='small' color='light'>
      {title}
    </Text>
    <Text
      as='dd'
      css={[title !== 'Forwarding Number' && styles.truncate, { lineHeight: theme.spacing(4), maxWidth: '288px' }]}
    >
      {children}
    </Text>
  </div>
);

export const OverrideSection = ({
  type,
  statusChip,
  startTime,
  endTime,
  mailboxId,
  media,
  forwardingNumber,

  openRemoveModal,
  openSettingModal,
  className,
}: OverrideSectionProps) => {
  const { t } = useTranslation('phone', { keyPrefix: 'overrides' });
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const { selectedLocationIds } = useAppScopeStore();
  const { aggregateValue: hasCallRoutesFlag } = FeatureFlagQueries.useAggregateFeatureFlagQuery({
    flagName: 'call-routes-settings',
    locationIds: selectedLocationIds ?? [],
  });
  const { data: voicemailBoxes } = useQuery({
    queryKey: [settingsTenantLocation?.phoneTenantId, ...queryKeys.settings.listVoicemailBox()],
    queryFn: () => VoicemailConfigAPI.List({ tenantId: settingsTenantLocation?.phoneTenantId ?? '' }),
    select: ({ voicemailBoxes }) => {
      let initialBoxes = voicemailBoxes;

      // Filter to only the voicemail boxes that the user has access to if the location is a unify location
      if (settingsTenantLocation?.locationType === 'unify') {
        // Children locations listed under a parent location include only the ones that the user has access to
        const availableLocationIds = settingsTenantLocation?.children?.map((loc) => loc.locationID) ?? [];

        initialBoxes = availableLocationIds.length
          ? voicemailBoxes.filter((box) => {
              return box.locationIds?.some((locationId) => availableLocationIds.includes(locationId));
            })
          : [];
      }

      // Filter again based on location selection in the dropdown
      return initialBoxes?.sort((a, b) => a.mailbox.name.localeCompare(b.mailbox.name));
    },
    enabled: !!settingsTenantLocation?.phoneTenantId,
    staleTime: 5 * 60 * 1000,
  });

  const voicemailBox = voicemailBoxes?.filter((vm) => vm.mailbox.id === mailboxId);

  return (
    <>
      <section className={className}>
        <Text color={'light'} size='small'>
          {t('Override Type')}
        </Text>
        <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
          <Heading level={3} css={overrideSectionHeadingStyle}>
            <div style={{ flex: 1 }}>
              <div css={overrideSectionChipStyle}>
                {type === OverrideType.OVERRIDE_TYPE_VMO ? t('Send to Voicemail') : t('Forward Call')}

                {statusChip}
              </div>{' '}
            </div>
            {hasCallRoutesFlag ? (
              <div style={{ display: 'flex', gap: theme.spacing(2) }}>
                <SecondaryButton
                  style={{ width: 'auto' }}
                  onClick={() => openSettingModal()}
                  trackingId='phone-portal-btn-openEditOverride'
                >
                  <Icon name='edit-small' />
                  {t('Edit')}
                </SecondaryButton>
                <SecondaryButton
                  destructive
                  css={{ width: 'auto' }}
                  onClick={() => openRemoveModal()}
                  trackingId='phone-portal-btn-removeOverride'
                >
                  <Icon name='remove-small' />
                  {t('Remove')}
                </SecondaryButton>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: theme.spacing(2) }}>
                <SecondaryButton
                  destructive
                  css={{ width: 'auto' }}
                  onClick={() => openRemoveModal()}
                  trackingId='phone-portal-btn-removeOverride'
                >
                  {t('Remove Override')}
                </SecondaryButton>
                <SecondaryButton
                  style={{ width: 'auto' }}
                  onClick={() => openSettingModal()}
                  trackingId='phone-portal-btn-openEditOverride'
                >
                  {t('Edit Override')}
                </SecondaryButton>
              </div>
            )}
          </Heading>

          <dl css={{ display: 'flex', gap: theme.spacing(5) }}>
            {type === OverrideType.OVERRIDE_TYPE_VMO && mailboxId && (
              <GridArea area='yoyo' title={t('Voicemail Box')}>
                {voicemailBox?.[0]?.mailbox?.name}
              </GridArea>
            )}

            {type === OverrideType.OVERRIDE_TYPE_VMO ? (
              <GridArea area='type' title={t('Voicemail Greeting')}>
                {media?.name}
              </GridArea>
            ) : (
              // <div style={{ gridArea: 'type', display: 'flex' }}>
              //   <DT>{t('Voicemail Greeting')}</DT>
              //   <DD>{media?.name}</DD>
              // </div>
              <GridArea area='type' title={t('Forwarding Number')}>
                {`${forwardingNumber?.name} (${forwardingNumber?.number})`}
              </GridArea>
              // <div style={{ gridArea: 'type' }}>
              //   <DT>{t('Forwarding Number')}</DT>
              //   <DD>{`${forwardingNumber?.name} (${forwardingNumber?.number})`}</DD>
              // </div>
            )}

            {type === OverrideType.OVERRIDE_TYPE_VMO && media?.id && (
              <GridArea area='media' title={t('Audio File')}>
                <CachedAudioScrubber filePath={media.path} mediaId={media.id} />
              </GridArea>
              // <div style={{ gridArea: 'media' }}>
              //   <DT>{t('Audio File')}</DT>
              //   <DD>
              //     <CachedAudioScrubber filePath={media.path} mediaId={media.id} />
              //   </DD>
              // </div>
            )}
          </dl>
          <dl css={{ display: 'flex', gap: theme.spacing(5) }}>
            {/* We also check if officeTimezone is defined b/c we don't want to show the wrong tz */}
            {startTime && (
              <GridArea area='start' title={t('Start Time')}>
                {startTime}
              </GridArea>
              // <div style={{ gridArea: 'start' }}>
              //   <DT>{t('Start Time')}</DT>
              //   <DD>{startTime}</DD>
              // </div>
            )}
            {/* We also check if officeTimezone is defined b/c we don't want to show the wrong tz */}
            {endTime && (
              <GridArea area='end' title={t('End Time')}>
                {endTime}
              </GridArea>
              // <div style={{ gridArea: 'end' }}>
              //   <DT>{t('End Time')}</DT>
              //   <DD>{endTime}</DD>
              // </div>
            )}
          </dl>
        </div>
      </section>
    </>
  );
};
